/**
 * ComplianceGoogleOUPage — /compliance/google-ou
 *
 * Three tabs:
 *   1. Templates — list, version history, view YAML body
 *   2. Audits    — list audit runs, click into per-policy diff with deep-link + AI explanation
 *   3. Settings  — OAuth status placeholder (wires once GCP project + OAuth client land)
 *
 * READ-ONLY ONLY.
 *   This module never writes Google policies. A red/orange banner pinned
 *   to the top makes the read-only constraint visible at all times. Each
 *   non-compliant policy gets a deep-link to the Google Admin Console —
 *   admins remediate manually. See the project plan:
 *   https://www.notion.so/37369d3aaf9e81438abce51aa38a92c1
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TableSortLabel,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Lock as LockIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as BackIcon,
  SmartToy as AiIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  HelpOutline as UnknownIcon,
  Search as SearchIcon,
  Devices as DevicesIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const SEVERITY_COLOR = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'default',
};

const DIFF_COLOR = {
  match: 'success',
  drift: 'warning',
  missing: 'error',
  extra: 'info',
};

// ── Template family categorization (Phase 2A) ─────────────────────────────
//
// Templates are tagged by family for dashboard grouping. Family membership
// is derived from the template `name` slug. New templates that do not match
// any pattern fall into "Other".
const TEMPLATE_FAMILIES = [
  {
    id: 'device-browser',
    label: 'Device & Browser',
    match: (name) => /^correctional-/.test(name),
    description:
      'Chrome OS + browser policies for correctional kiosk and non-kiosk OUs.',
  },
  {
    id: 'workspace-security',
    label: 'Workspace Security',
    match: (name) =>
      /^workspace-security-/.test(name) || /^workspace-ssoconfig/.test(name),
    description: '2-Step Verification, password requirements, SAML SSO.',
  },
  {
    id: 'data-governance',
    label: 'Data Governance',
    match: (name) => /^workspace-drive-/.test(name),
    description: 'Drive sharing rules, external sharing posture.',
  },
  {
    id: 'identity-domain',
    label: 'Identity & Domain',
    match: (name) =>
      /^workspace-domain-/.test(name) ||
      /^workspace-customer-/.test(name) ||
      /^workspace-license-/.test(name),
    description:
      'Customer tenant identity, verified domains, license assignments.',
  },
  {
    id: 'mobile',
    label: 'Mobile',
    match: (name) => /^workspace-mobile-/.test(name),
    description: 'Mobile device management posture.',
  },
  {
    id: 'conditional-access',
    label: 'Conditional Access',
    match: (name) => /^workspace-context-aware/.test(name),
    description:
      'Context-Aware Access policies, device-trust requirements.',
  },
  {
    id: 'other',
    label: 'Other',
    match: () => true, // catch-all — must stay last
    description: 'Templates without an explicit family assignment yet.',
  },
];

function familyForTemplateName(name) {
  for (const fam of TEMPLATE_FAMILIES) {
    if (fam.match(name)) return fam;
  }
  return TEMPLATE_FAMILIES[TEMPLATE_FAMILIES.length - 1];
}

// ── Banner ────────────────────────────────────────────────────────────────
export function ReadOnlyBanner({ warning }) {
  return (
    <Alert
      severity="info"
      icon={<LockIcon />}
      sx={{
        mb: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: '#0288d1',
        fontWeight: 600,
      }}
    >
      <AlertTitle sx={{ fontWeight: 'bold' }}>READ-ONLY MODULE</AlertTitle>
      {warning ||
        'Automatrix never writes Google policies. Use the Admin Console deep-links beside each drift entry to remediate manually.'}
    </Alert>
  );
}

// ── Tab: Templates ────────────────────────────────────────────────────────
export function TemplatesTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTemplates(data.data || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Group by name, latest version first.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const t of templates) {
      if (!m.has(t.name)) m.set(t.name, []);
      m.get(t.name).push(t);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => b.version - a.version);
    }
    return Array.from(m.entries());
  }, [templates]);

  // Bucket by family for Phase 2A categorization. Families with no
  // templates loaded are dropped from the render. Within a family the
  // template-name groups are sorted alphabetically.
  const byFamily = useMemo(() => {
    const buckets = new Map();
    for (const fam of TEMPLATE_FAMILIES) buckets.set(fam.id, []);
    for (const [name, versions] of grouped) {
      const fam = familyForTemplateName(name);
      buckets.get(fam.id).push([name, versions]);
    }
    for (const arr of buckets.values()) {
      arr.sort((a, b) => a[0].localeCompare(b[0]));
    }
    return TEMPLATE_FAMILIES.map((fam) => ({
      ...fam,
      entries: buckets.get(fam.id) || [],
    })).filter((fam) => fam.entries.length > 0);
  }, [grouped]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Templates
        </Typography>
        <Tooltip title="Reload from disk">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        YAML templates in <code>src/backend/nest-api/templates/google-ou/</code>.
        Each describes the expected Chrome / Workspace policy posture for a
        deployment surface.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : byFamily.length === 0 ? (
        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No templates loaded. Drop a YAML file into
              <code> templates/google-ou/</code> and click reload.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Box>
          {byFamily.map((fam) => (
            <Box key={fam.id} sx={{ mb: 3 }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {fam.label}{' '}
                  <Chip
                    size="small"
                    label={fam.entries.length}
                    sx={{ ml: 0.5 }}
                  />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fam.description}
                </Typography>
              </Box>
              <Paper>
                {fam.entries.map(([name, versions]) => {
                  const latest = versions[0];
                  return (
                    <Accordion key={name} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1">
                            {latest.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {name} · v{latest.version} ·{' '}
                            <Chip
                              size="small"
                              label={latest.appliesTo}
                              sx={{ ml: 0.5 }}
                            />
                            {versions.length > 1 &&
                              ` · ${versions.length} versions`}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {latest.description && (
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {latest.description}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelected(latest)}
                        >
                          View parsed body
                        </Button>
                        {versions.length > 1 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption">
                              Version history:
                            </Typography>
                            <Table size="small">
                              <TableBody>
                                {versions.map((v) => (
                                  <TableRow key={v.id}>
                                    <TableCell width={80}>v{v.version}</TableCell>
                                    <TableCell>{v.sourcePath}</TableCell>
                                    <TableCell width={120}>
                                      <Button
                                        size="small"
                                        onClick={() => setSelected(v)}
                                      >
                                        view
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle>
              {selected.displayName}
              <Typography variant="caption" display="block" color="text.secondary">
                {selected.name} v{selected.version}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="caption" color="text.secondary">
                Source: <code>{selected.sourcePath}</code>
                <br />
                SHA-256: <code>{selected.sourceHash}</code>
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 2,
                  p: 1,
                  bgcolor: '#f5f5f5',
                  fontSize: 12,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 500,
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                }}
              >
                {JSON.stringify(selected.body, null, 2)}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ── Tab: Audits ───────────────────────────────────────────────────────────
//
// We bound the `/ous` fetch so the Run dialog never sits forever on a
// "Loading OUs from Google Workspace…" placeholder — the most common failure
// modes (SA key unreadable, impersonate-user mis-set) manifest as slow
// requests that eventually 5xx. 15s is generous enough for a cold Admin SDK
// hit (~3-5s typical) while still surfacing real problems quickly.
const OU_FETCH_TIMEOUT_MS = 15_000;

// Sort heuristic for the OU autocomplete: bubble the most commonly-audited
// OUs (the facility-level Kiosk / Non-Kiosk OUs) to the top, then
// alphabetize the rest. Tightens the picker for the 80% case where the
// auditor wants a /Sites/<facility>/{Kiosk,Non-Kiosk} OU.
function compareOusForPicker(a, b) {
  const isPriorityOu = (o) => /Kiosk|Non-Kiosk/i.test(o.path || '');
  const aPri = isPriorityOu(a);
  const bPri = isPriorityOu(b);
  if (aPri && !bPri) return -1;
  if (!aPri && bPri) return 1;
  return (a.path || '').localeCompare(b.path || '');
}

// templateName default:
//
//   Demo target is `correctional-kiosk` (the loader returns the latest
//   version for a given slug automatically — currently v2). We default to
//   that slug if it shows up in the /templates response. If not (e.g. it
//   gets renamed again or the templates dir is empty), we fall back to the
//   FIRST entry the API returns (sorted name ASC, version DESC by
//   TemplateLoaderService). The runtime fallback survives another rename
//   without code changes — we just hit the `correctional-tablet-kiosk` →
//   `correctional-kiosk` rename during demo prep, so the lesson is fresh.
const PREFERRED_TEMPLATE_SLUG = 'correctional-kiosk';

function AuditsTab({ token, openWithAuditId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audits, setAudits] = useState([]);
  const [detailId, setDetailId] = useState(openWithAuditId || null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runState, setRunState] = useState({
    templateName: '',
    ouId: '',
    ouDisplayName: '',
    notes: '',
    source: 'live',
  });
  const [running, setRunning] = useState(false);

  // Template picker for the Run dialog. We load the list locally so the user
  // picks from a Select instead of typing a slug (typos used to create
  // audits against templates that did not exist).
  const [templates, setTemplates] = useState([]);

  // OU directory for the autocomplete dropdown.
  const [ous, setOus] = useState([]);
  const [ousLoading, setOusLoading] = useState(false);
  // Structured `/ous` failure { kind, message }. `kind` drives the UX:
  //   - 'timeout'      → "Failed to load OUs from Google. Click to retry."
  //   - 'unauthorized' → "Session expired. Please log out and back in."
  //   - 'forbidden'    → "Connect Service Account in Settings tab"
  //   - 'other'        → raw message fallback
  const [ousError, setOusError] = useState(null);
  // Bump to re-trigger the /ous fetch effect for retry.
  const [ousReloadKey, setOusReloadKey] = useState(0);
  const [selectedOu, setSelectedOu] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/audits?limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAudits(data.data || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Templates dropdown — fetched once. Default selection logic:
  //   1. If PREFERRED_TEMPLATE_SLUG (`correctional-kiosk`) is in the
  //      response, pick that — the loader returns the latest version
  //      automatically for a given slug.
  //   2. Else fall back to the FIRST entry the API returns. The API sorts
  //      name ASC, version DESC, so this is the latest version of the
  //      alphabetically-first template.
  //   3. If the request fails the dialog still opens — the Run button stays
  //      disabled until the user picks something manually.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Per-OU audit dialog: exclude `workspace-tenant` templates — those
        // scope at the customer level and live in the "Tenant Policies"
        // tab. Audits the user kicks off here are always per-OU.
        const resp = await fetch(
          '/api/google-ou-compliance/templates?appliesTo=!workspace-tenant',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (cancelled) return;
        const list = data.data || [];
        setTemplates(list);
        if (list.length > 0) {
          const preferred = list.find(
            (t) => t.name === PREFERRED_TEMPLATE_SLUG,
          );
          const seed = preferred ? preferred.name : list[0].name;
          setRunState((s) =>
            s.templateName ? s : { ...s, templateName: seed },
          );
        }
      } catch (e) {
        if (!cancelled) {
          // Non-fatal — log so the operator can diagnose, but don't block
          // the Audits view from rendering.
          // eslint-disable-next-line no-console
          console.warn('[ComplianceGoogleOU] templates load failed:', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Fetch the OU directory once when the run dialog first opens (or on
  // retry). Wrapped with AbortController + 15s timeout so a hung Admin SDK
  // call surfaces as "Failed to load OUs" instead of an indefinite spinner.
  useEffect(() => {
    if (!runDialogOpen) return undefined;
    if (ous.length > 0 || ousLoading) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    const timeoutId = setTimeout(() => controller.abort(), OU_FETCH_TIMEOUT_MS);

    (async () => {
      setOusLoading(true);
      setOusError(null);
      try {
        const resp = await fetch('/api/google-ou-compliance/ous', {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (resp.status === 401) {
          // eslint-disable-next-line no-throw-literal
          throw { kind: 'unauthorized', message: 'HTTP 401' };
        }
        if (resp.status === 403) {
          const text = await resp.text().catch(() => '');
          // eslint-disable-next-line no-throw-literal
          throw { kind: 'forbidden', message: text || 'HTTP 403' };
        }
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          // eslint-disable-next-line no-throw-literal
          throw {
            kind: 'other',
            message: `HTTP ${resp.status}${text ? `: ${text}` : ''}`,
          };
        }
        const data = await resp.json();
        if (!cancelled) setOus(data.data || []);
      } catch (raw) {
        if (cancelled) return;
        // Native fetch surfaces aborts as DOMException name='AbortError'.
        // Tagged objects come from our status-code branches above.
        if (raw && raw.name === 'AbortError') {
          setOusError({
            kind: 'timeout',
            message: `Request timed out after ${OU_FETCH_TIMEOUT_MS / 1000}s`,
          });
        } else if (raw && raw.kind) {
          setOusError(raw);
        } else {
          setOusError({
            kind: 'other',
            message: (raw && raw.message) || String(raw),
          });
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setOusLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [runDialogOpen, token, ous.length, ousLoading, ousReloadKey]);

  // Memoized + sorted list for the autocomplete. Priority OUs (Kiosk /
  // Non-Kiosk) bubble to the top so the picker is fast for the 80% case.
  const sortedOus = useMemo(() => {
    const copy = [...ous];
    copy.sort(compareOusForPicker);
    return copy;
  }, [ous]);

  const retryOusFetch = useCallback(() => {
    setOusError(null);
    setOusReloadKey((k) => k + 1);
  }, []);

  const runAudit = async () => {
    setRunning(true);
    setError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/audits/run', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateName: runState.templateName,
          ouId: runState.ouId,
          ouDisplayName: runState.ouDisplayName,
          notes: runState.notes,
          source: runState.source,
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${t}`);
      }
      const data = await resp.json();
      setRunDialogOpen(false);
      await load();
      setDetailId(data.auditId);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  if (detailId) {
    return (
      <AuditDetail
        token={token}
        auditId={detailId}
        onBack={() => setDetailId(null)}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Audit runs
        </Typography>
        <Button
          variant="contained"
          startIcon={<RunIcon />}
          onClick={() => setRunDialogOpen(true)}
        >
          Run audit
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} sx={{ ml: 1 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Live audits hit the Chrome Policy API <code>:resolve</code> endpoint
        via Service Account + Domain-Wide Delegation (Settings tab). Mock
        audits run against the bundled fixture at{' '}
        <code>test/fixtures/remedynetwork-mock-snapshot.json</code> for
        offline testing. Both paths are read-only.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>OU</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Match</TableCell>
                <TableCell align="right">Drift</TableCell>
                <TableCell align="right">Missing</TableCell>
                <TableCell align="right">Extra</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No audits yet. Click "Run audit" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {audits.map((a) => {
                const s = a.summaryStats || {};
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>{new Date(a.startedAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {a.templateName} <Chip size="small" label={`v${a.templateVersion}`} />
                    </TableCell>
                    <TableCell>
                      {a.ouDisplayName || a.ouId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.source}
                        color={a.source === 'live' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.status}
                        color={
                          a.status === 'completed'
                            ? 'success'
                            : a.status === 'failed'
                            ? 'error'
                            : 'info'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">{s.matchCount ?? 0}</TableCell>
                    <TableCell align="right">
                      {(s.driftCount ?? 0) > 0 ? (
                        <Chip size="small" label={s.driftCount} color="warning" />
                      ) : (
                        s.driftCount ?? 0
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(s.missingCount ?? 0) > 0 ? (
                        <Chip size="small" label={s.missingCount} color="error" />
                      ) : (
                        s.missingCount ?? 0
                      )}
                    </TableCell>
                    <TableCell align="right">{s.extraCount ?? 0}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setDetailId(a.id)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={runDialogOpen} onClose={() => !running && setRunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run audit (READ-ONLY)</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            READ-ONLY. The live source calls only the Chrome Policy API{' '}
            <code>:resolve</code> endpoint. No write APIs are ever called.
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Source
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label="live"
                color={runState.source === 'live' ? 'primary' : 'default'}
                onClick={() => setRunState((s) => ({ ...s, source: 'live' }))}
                size="small"
              />
              <Chip
                label="mock"
                color={runState.source === 'mock' ? 'primary' : 'default'}
                onClick={() => setRunState((s) => ({ ...s, source: 'mock' }))}
                size="small"
              />
            </Box>
          </Box>
          {/* Template picker — Select rather than free-text TextField so the
              user can't typo a non-existent slug. Default selection is the
              first row from the /templates response (latest version of the
              alphabetically-first template per the API sort). */}
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="run-audit-template-label">Template</InputLabel>
            <Select
              labelId="run-audit-template-label"
              label="Template"
              value={runState.templateName}
              onChange={(e) =>
                setRunState((s) => ({ ...s, templateName: e.target.value }))
              }
            >
              {templates.length === 0 && (
                <MenuItem value="" disabled>
                  (no templates loaded)
                </MenuItem>
              )}
              {/* Collapse multi-version groups: show only the highest-version
                  entry per name. The API returns name ASC, version DESC, so
                  the first occurrence of each name IS the latest. */}
              {Array.from(
                templates
                  .reduce((m, t) => {
                    if (!m.has(t.name)) m.set(t.name, t);
                    return m;
                  }, new Map())
                  .values(),
              ).map((t) => (
                <MenuItem key={t.name} value={t.name}>
                  {(t.displayName || t.name)}
                  {typeof t.version === 'number' && ` (v${t.version})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            options={sortedOus}
            value={selectedOu}
            loading={ousLoading}
            disabled={!!ousError}
            disableClearable={false}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) => (o ? o.path : '')}
            onChange={(_, val) => {
              setSelectedOu(val);
              setRunState((s) => ({
                ...s,
                ouId: val ? val.id : '',
                ouDisplayName: val ? val.name : '',
              }));
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'block !important', py: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {option.path}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.id}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organizational Unit"
                placeholder={
                  ousLoading
                    ? 'Loading OUs from Google Workspace…'
                    : 'Search by path (e.g. /Sites/WDOC/Kiosk)'
                }
                fullWidth
                margin="normal"
                helperText={
                  ousError
                    ? null /* error rendered via the Alert below */
                    : ous.length > 0
                    ? `${ous.length} OUs available from Workspace Admin SDK`
                    : 'Connect Service Account in Settings tab first'
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {ousLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {/* Inline /ous failure surface. We branch on the error `kind` so
              the message is actionable — a generic "OU directory unavailable"
              left the operator guessing whether to re-auth, re-config, or
              just wait it out. */}
          {ousError && (
            <Alert
              severity={ousError.kind === 'unauthorized' ? 'warning' : 'error'}
              sx={{ mt: 1, mb: 1 }}
              action={
                ousError.kind === 'timeout' || ousError.kind === 'other' ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryOusFetch}
                    disabled={ousLoading}
                  >
                    Retry
                  </Button>
                ) : null
              }
            >
              {ousError.kind === 'timeout' && (
                <>Failed to load OUs from Google. Click Retry to try again.</>
              )}
              {ousError.kind === 'unauthorized' && (
                <>Session expired. Please log out and back in.</>
              )}
              {ousError.kind === 'forbidden' &&
                (/service account|not configured|dwd|impersonate/i.test(
                  ousError.message || '',
                ) ? (
                  <>Connect Service Account in Settings tab.</>
                ) : (
                  <>OU directory denied (403): {ousError.message}</>
                ))}
              {ousError.kind === 'other' && (
                <>OU directory unavailable: {ousError.message}</>
              )}
            </Alert>
          )}
          <TextField
            label="Notes"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={runState.notes}
            onChange={(e) => setRunState((s) => ({ ...s, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button
            onClick={runAudit}
            variant="contained"
            disabled={running || !runState.ouId || !runState.templateName}
          >
            {running ? 'Running…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Audit detail ──────────────────────────────────────────────────────────
export function AuditDetail({ token, auditId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audit, setAudit] = useState(null);
  const [diffs, setDiffs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`/api/google-ou-compliance/audits/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAudit(data.audit);
      setDiffs(data.diffs || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [auditId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const driftLike = diffs.filter((d) => d.diffType !== 'match');
  const matches = diffs.filter((d) => d.diffType === 'match');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
          Audit detail
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading || !audit ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Run metadata
            </Typography>
            <Typography variant="body2">
              Template: <strong>{audit.templateName}</strong> v{audit.templateVersion}
              {'  ·  '}
              OU: <strong>{audit.ouDisplayName || audit.ouId}</strong>
              {'  ·  '}
              Source: <Chip size="small" label={audit.source} />
              {'  ·  '}
              Status: <Chip
                size="small"
                label={audit.status}
                color={audit.status === 'completed' ? 'success' : 'default'}
              />
            </Typography>
            {audit.errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {audit.errorMessage}
              </Alert>
            )}
            {audit.notes && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Notes: {audit.notes}
              </Typography>
            )}
          </Paper>

          <Typography variant="subtitle1" gutterBottom>
            Non-compliant policies ({driftLike.length})
          </Typography>
          {driftLike.length === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              All template policies match the snapshot.
            </Alert>
          ) : (
            driftLike.map((d) => <DiffCard key={d.id} diff={d} />)
          )}

          {matches.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  {matches.length} compliant policies (collapsed)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {matches.map((d) => (
                  <Typography key={d.id} variant="body2">
                    ✓ {d.policySchema}
                  </Typography>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
}

export function DiffCard({ diff }) {
  return (
    <Paper sx={{ p: 2, mb: 2, borderLeft: `4px solid ${severityColor(diff.severity)}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Chip
          size="small"
          label={diff.diffType}
          color={DIFF_COLOR[diff.diffType] || 'default'}
          sx={{ mr: 1 }}
        />
        <Chip
          size="small"
          label={diff.severity}
          color={SEVERITY_COLOR[diff.severity] || 'default'}
          sx={{ mr: 1 }}
        />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {diff.policyDisplayName || diff.policySchema}
        </Typography>
        {diff.adminConsoleUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<LaunchIcon />}
            href={diff.adminConsoleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Admin Console
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {diff.policySchema}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Expected (template)
          </Typography>
          <JsonBlock value={diff.expectedValue} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Actual (snapshot)
          </Typography>
          <JsonBlock value={diff.actualValue} />
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <AiIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            AI explanation (not authoritative)
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {diff.aiExplanation || '(no explanation generated)'}
        </Typography>
      </Box>

      {diff.remediationHint && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Template author hint
          </Typography>
          <Typography variant="body2">{diff.remediationHint}</Typography>
        </>
      )}
    </Paper>
  );
}

function severityColor(sev) {
  switch (sev) {
    case 'critical':
      return '#d32f2f';
    case 'high':
      return '#f57c00';
    case 'medium':
      return '#fbc02d';
    case 'low':
      return '#0288d1';
    default:
      return '#9e9e9e';
  }
}

export function JsonBlock({ value }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        bgcolor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        fontSize: 11,
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        maxHeight: 200,
        overflow: 'auto',
      }}
    >
      {value == null ? '(none)' : JSON.stringify(value, null, 2)}
    </Box>
  );
}

// ── Tab: Fleet (the demo money shot) ─────────────────────────────────────
const STATUS_META = {
  compliant: { label: 'Compliant', color: 'success', Icon: CheckIcon },
  drift: { label: 'Drift', color: 'warning', Icon: WarningIcon },
  missing: { label: 'Missing policies', color: 'error', Icon: ErrorIcon },
  failed: { label: 'Failed', color: 'error', Icon: ErrorIcon },
  running: { label: 'Running', color: 'info', Icon: RefreshIcon },
  'not-audited': { label: 'Not audited', color: 'default', Icon: UnknownIcon },
};

function statusChip(status) {
  const m = STATUS_META[status] || STATUS_META['not-audited'];
  const Icon = m.Icon;
  return (
    <Chip
      size="small"
      icon={<Icon style={{ fontSize: 14 }} />}
      label={m.label}
      color={m.color}
      variant={status === 'not-audited' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 500 }}
    />
  );
}

function countCell(value, kind) {
  if (value === 0 || value == null) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        —
      </Typography>
    );
  }
  let bg = '#f5f5f5';
  let fg = '#424242';
  let weight = 500;
  if (kind === 'match') {
    bg = '#e8f5e9';
    fg = '#1b5e20';
    weight = 600;
  } else if (kind === 'drift' && value > 0) {
    bg = '#fff3e0';
    fg = '#e65100';
    weight = 700;
  } else if (kind === 'missing' && value > 0) {
    bg = '#ffebee';
    fg = '#b71c1c';
    weight = 700;
  } else if (kind === 'extra' && value > 0) {
    bg = '#eceff1';
    fg = '#37474f';
  }
  return (
    <Box
      sx={{
        display: 'inline-block',
        minWidth: 36,
        textAlign: 'center',
        bgcolor: bg,
        color: fg,
        px: 1.25,
        py: 0.25,
        borderRadius: 1,
        fontWeight: weight,
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Box>
  );
}

function FleetTab({ token, onOpenAudit }) {
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  // Per-OU template assignments keyed by ouId. Each entry:
  //   { templateName, source: 'saved' | 'heuristic', suggested, ... }
  const [assignmentByOuId, setAssignmentByOuId] = useState({});
  // Track which OUs have a pending save (for the "saved" flash indicator).
  const [savingOuIds, setSavingOuIds] = useState({});
  const [recentlySaved, setRecentlySaved] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [sortKey, setSortKey] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  // "Run all audits" progress dialog.
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkStarting, setBulkStarting] = useState(false);
  // Phase 2C — per-OU device aggregates keyed by ouId.
  const [deviceAggByOuId, setDeviceAggByOuId] = useState({});
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [chromeMgmtEnabled, setChromeMgmtEnabled] = useState(true);
  const [enableUrl, setEnableUrl] = useState(null);
  const [drillOuPath, setDrillOuPath] = useState(null);
  const [drillOuId, setDrillOuId] = useState(null);
  // Fleet-wide device drill-down (clickable tiles → table dialog).
  const [deviceDrill, setDeviceDrill] = useState({
    open: false,
    filter: 'all',
    title: 'All devices',
  });

  useEffect(() => {
    (async () => {
      try {
        // Fleet tab is per-OU. Exclude workspace-tenant templates — those
        // live in the "Tenant Policies" tab and audit at the customer
        // scope, not per-OU.
        const resp = await fetch(
          '/api/google-ou-compliance/templates?appliesTo=!workspace-tenant',
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setTemplates(data.data || []);
      } catch (e) {
        setError(`Templates load failed: ${e.message || String(e)}`);
      }
    })();
  }, [token]);

  const loadAssignments = useCallback(async () => {
    try {
      const resp = await fetch('/api/google-ou-compliance/ou-assignments', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const m = {};
      for (const a of data.data || []) {
        m[a.ouId] = a;
      }
      setAssignmentByOuId(m);
    } catch (e) {
      // Soft-fail — fleet view can still render without assignment metadata,
      // it just won't show the per-OU template picker until this loads.
    }
  }, [token]);

  const loadFleet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `/api/google-ou-compliance/audits/fleet-by-assignment`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setRows(data.data || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadFleet();
    loadAssignments();
  }, [loadFleet, loadAssignments]);

  // Phase 2C — load per-OU device aggregates in the background. We pick the
  // first available template (the endpoint scopes to a template's fleet view).
  // Soft-fails: device columns simply stay empty if the endpoint errors.
  const loadDeviceAggregates = useCallback(async () => {
    if (templates.length === 0) return;
    const firstTemplate = templates[0]?.name;
    if (!firstTemplate) return;
    setDeviceLoading(true);
    try {
      const resp = await fetch(
        `/api/google-ou-compliance/fleet/devices?templateName=${encodeURIComponent(
          firstTemplate,
        )}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const m = {};
      for (const r of data.rows || []) {
        m[r.ouId] = r;
      }
      setDeviceAggByOuId(m);
      setChromeMgmtEnabled(data.chromeMgmtEnabled !== false);
      setEnableUrl(data.enableUrl || null);
    } catch (e) {
      // Soft-fail.
    } finally {
      setDeviceLoading(false);
    }
  }, [token, templates]);

  useEffect(() => {
    loadDeviceAggregates();
  }, [loadDeviceAggregates]);

  // Save an assignment change. Optimistically updates local state, posts to
  // the API, then re-loads the fleet (so the row's templateName + audit
  // status reflect the new assignment).
  const saveAssignment = useCallback(
    async (row, newTemplateName) => {
      const ouId = row.ouId;
      setSavingOuIds((prev) => ({ ...prev, [ouId]: true }));
      // Optimistic local update so the dropdown reflects the new value
      // immediately. We mark it as "saved" so the italic/suggested treatment
      // drops off right away.
      setAssignmentByOuId((prev) => ({
        ...prev,
        [ouId]: {
          ouId,
          ouPath: row.ouPath,
          templateName: newTemplateName,
          source: 'saved',
          suggested: false,
        },
      }));
      try {
        const resp = await fetch(
          `/api/google-ou-compliance/ou-assignments/${encodeURIComponent(ouId)}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              templateName: newTemplateName,
              ouPath: row.ouPath,
            }),
          },
        );
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(
            errBody?.message || `Save failed: HTTP ${resp.status}`,
          );
        }
        setRecentlySaved((prev) => ({ ...prev, [ouId]: Date.now() }));
        // Clear the saved flash after a short delay.
        setTimeout(() => {
          setRecentlySaved((prev) => {
            const next = { ...prev };
            delete next[ouId];
            return next;
          });
        }, 2500);
        // Reload the fleet so the audit state reflects the new template.
        await loadFleet();
      } catch (e) {
        setError(`Save failed for ${row.ouPath}: ${e.message || String(e)}`);
        // Roll back optimistic update.
        await loadAssignments();
      } finally {
        setSavingOuIds((prev) => {
          const next = { ...prev };
          delete next[ouId];
          return next;
        });
      }
    },
    [token, loadFleet, loadAssignments],
  );

  // ── "Run all audits" flow ─────────────────────────────────────────────
  const startBulkAudit = useCallback(async () => {
    setBulkStarting(true);
    setError(null);
    try {
      const resp = await fetch(
        '/api/google-ou-compliance/ou-assignments/bulk-audit',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(
          errBody?.message || `Bulk audit start failed: HTTP ${resp.status}`,
        );
      }
      const data = await resp.json();
      setBulkJob(data.job);
      setBulkDialogOpen(true);
      setBulkConfirmOpen(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBulkStarting(false);
    }
  }, [token]);

  // Poll the bulk job while open. Stops once the job hits a terminal state.
  useEffect(() => {
    if (!bulkDialogOpen || !bulkJob?.jobId) return undefined;
    if (
      bulkJob.status === 'completed' ||
      bulkJob.status === 'failed' ||
      bulkJob.status === 'cancelled'
    ) {
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        const resp = await fetch(
          `/api/google-ou-compliance/ou-assignments/bulk-audit/${bulkJob.jobId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;
        const data = await resp.json();
        setBulkJob(data.job);
        if (
          data.job.status === 'completed' ||
          data.job.status === 'failed' ||
          data.job.status === 'cancelled'
        ) {
          // Auto-reload the fleet when done.
          loadFleet();
        }
      } catch (e) {
        // Soft fail — keep polling.
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [bulkDialogOpen, bulkJob?.jobId, bulkJob?.status, token, loadFleet]);

  const cancelBulkAudit = useCallback(async () => {
    if (!bulkJob?.jobId) return;
    try {
      await fetch(
        `/api/google-ou-compliance/ou-assignments/bulk-audit/${bulkJob.jobId}/cancel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (e) {
      // ignore
    }
  }, [bulkJob?.jobId, token]);

  const stats = useMemo(() => {
    const s = {
      total: rows.length,
      compliant: 0,
      drift: 0,
      missing: 0,
      notAudited: 0,
      failed: 0,
    };
    for (const r of rows) {
      if (r.status === 'compliant') s.compliant += 1;
      else if (r.status === 'drift') s.drift += 1;
      else if (r.status === 'missing') s.missing += 1;
      else if (r.status === 'failed') s.failed += 1;
      else if (r.status === 'not-audited') s.notAudited += 1;
    }
    const audited = s.total - s.notAudited;
    s.compliancePct = audited > 0 ? Math.round((s.compliant / audited) * 100) : 0;
    return s;
  }, [rows]);

  // Phase 2C — fleet-wide device aggregates derived from the per-OU rows.
  const deviceStats = useMemo(() => {
    let totalDevices = 0;
    let active = 0;
    let inactive = 0;
    let deprovisioned = 0;
    let latestExtCount = 0;
    let extInstallTotal = 0;
    const histogram = { within1d: 0, within7d: 0, within30d: 0, within90d: 0, older: 0 };
    const medianAges = [];
    for (const r of Object.values(deviceAggByOuId)) {
      totalDevices += r.deviceCount || 0;
      active += r.activeCount || 0;
      inactive += r.inactiveCount || 0;
      deprovisioned += r.deprovisionedCount || 0;
      const versions = r.versionHistogram || [];
      const ouTotal = versions.reduce((a, v) => a + (v.count || 0), 0);
      if (ouTotal > 0 && versions[0]) {
        extInstallTotal += ouTotal;
        latestExtCount += versions[0].count || 0;
      }
      const h = r.lastSyncHistogram || {};
      histogram.within1d += h.within1d || 0;
      histogram.within7d += h.within7d || 0;
      histogram.within30d += h.within30d || 0;
      histogram.within90d += h.within90d || 0;
      histogram.older += h.older || 0;
      if (typeof r.lastSyncMedianAgeMs === 'number') {
        medianAges.push(r.lastSyncMedianAgeMs);
      }
    }
    medianAges.sort((a, b) => a - b);
    const medianAge =
      medianAges.length > 0 ? medianAges[Math.floor(medianAges.length / 2)] : null;
    const latestExtPct =
      extInstallTotal > 0
        ? Math.round((latestExtCount / extInstallTotal) * 10000) / 100
        : null;
    const syncedTotal =
      histogram.within1d +
      histogram.within7d +
      histogram.within30d +
      histogram.within90d +
      histogram.older;
    const pct = (n) =>
      syncedTotal > 0 ? Math.round((n / syncedTotal) * 1000) / 10 : 0;
    return {
      totalDevices,
      active,
      inactive,
      deprovisioned,
      latestExtPct,
      histogram,
      medianAgeMs: medianAge,
      pctWithin24h: pct(histogram.within1d),
      pctWithin7d: pct(histogram.within1d + histogram.within7d),
      pctWithin30d: pct(
        histogram.within1d + histogram.within7d + histogram.within30d,
      ),
    };
  }, [deviceAggByOuId]);

  // Effective template for a row: explicit assignment if present, else
  // the fleet row's `templateName` (which the backend fills in with the
  // suggestion for un-assigned OUs in the fleet-by-assignment endpoint).
  const effectiveTemplate = useCallback(
    (row) => {
      const a = assignmentByOuId[row.ouId];
      if (a?.templateName) return a.templateName;
      return row.templateName || 'correctional-kiosk';
    },
    [assignmentByOuId],
  );

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter(
        (r) =>
          (r.ouPath || '').toLowerCase().includes(q) ||
          (r.ouId || '').toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'all') {
      out = out.filter((r) => r.status === statusFilter);
    }
    if (templateFilter !== 'all') {
      out = out.filter((r) => effectiveTemplate(r) === templateFilter);
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const statusRank = {
      missing: 0,
      drift: 1,
      failed: 2,
      running: 3,
      compliant: 4,
      'not-audited': 5,
    };
    const cmp = (a, b) => {
      let av;
      let bv;
      if (sortKey === 'ou') {
        av = (a.ouPath || '').toLowerCase();
        bv = (b.ouPath || '').toLowerCase();
      } else if (sortKey === 'status') {
        av = statusRank[a.status] ?? 9;
        bv = statusRank[b.status] ?? 9;
      } else if (sortKey === 'auditedAt') {
        av = a.auditedAt || '';
        bv = b.auditedAt || '';
      } else if (sortKey === 'template') {
        av = effectiveTemplate(a);
        bv = effectiveTemplate(b);
      } else {
        av = a[sortKey] ?? 0;
        bv = b[sortKey] ?? 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    };
    return [...out].sort(cmp);
  }, [rows, filter, statusFilter, templateFilter, sortKey, sortDir, effectiveTemplate]);

  // Unique templates currently in use (assigned or suggested), for the
  // filter chip dropdown.
  const usedTemplateNames = useMemo(() => {
    const set = new Set();
    for (const r of rows) set.add(effectiveTemplate(r));
    return Array.from(set).sort();
  }, [rows, effectiveTemplate]);

  // Deduped templates list (latest version per name) for the per-OU
  // dropdown options.
  const templateOptions = useMemo(() => {
    return Array.from(new Map(templates.map((t) => [t.name, t])).values());
  }, [templates]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Fleet compliance
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RunIcon />}
          onClick={() => setBulkConfirmOpen(true)}
          disabled={loading || rows.length === 0 || bulkStarting}
        >
          Run all audits
        </Button>
        <Tooltip title="Reload">
          <span>
            <IconButton onClick={() => { loadFleet(); loadAssignments(); }} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each OU is audited against its own assigned template. Italic template
        names are heuristic suggestions — change the dropdown on any row to
        save an explicit assignment (visible to all users). "Run all audits"
        fires a read-only audit per OU against its assigned template.
      </Typography>

      {!chromeMgmtEnabled && enableUrl && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              endIcon={<LaunchIcon />}
              href={enableUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Enable
            </Button>
          }
        >
          Chrome Management API is not yet enabled in the GCP project — the
          extension-version distribution columns will populate once the API
          is enabled (1–5 min after clicking enable). Device columns continue
          to render from the Admin SDK Directory API.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}>
        <StatCard label="OUs in scope" value={stats.total} color="#1976d2" bg="#e3f2fd" />
        <StatCard
          label="Compliant"
          value={stats.compliant}
          color="#1b5e20"
          bg="#e8f5e9"
          subline={`${stats.compliancePct}% of audited`}
        />
        <StatCard label="Drift" value={stats.drift} color="#e65100" bg="#fff3e0" />
        <StatCard label="Missing" value={stats.missing} color="#b71c1c" bg="#ffebee" />
        <StatCard label="Not audited" value={stats.notAudited} color="#616161" bg="#f5f5f5" />
      </Stack>

      {deviceStats.totalDevices > 0 && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}
        >
          <StatCard
            label="Devices"
            value={deviceStats.totalDevices.toLocaleString()}
            color="#1976d2"
            bg="#e3f2fd"
            subline={`${deviceStats.active.toLocaleString()} active / ${deviceStats.inactive.toLocaleString()} inactive — click to drill`}
            onClick={() =>
              setDeviceDrill({ open: true, filter: 'all', title: 'All devices' })
            }
          />
          <StatCard
            label="On latest ext"
            value={
              deviceStats.latestExtPct == null
                ? '—'
                : `${deviceStats.latestExtPct}%`
            }
            color={
              deviceStats.latestExtPct == null
                ? '#616161'
                : deviceStats.latestExtPct >= 95
                ? '#1b5e20'
                : deviceStats.latestExtPct >= 80
                ? '#e65100'
                : '#b71c1c'
            }
            bg={
              deviceStats.latestExtPct == null
                ? '#f5f5f5'
                : deviceStats.latestExtPct >= 95
                ? '#e8f5e9'
                : deviceStats.latestExtPct >= 80
                ? '#fff3e0'
                : '#ffebee'
            }
            subline={
              chromeMgmtEnabled
                ? 'click to see out-of-date'
                : 'pending Chrome Mgmt API enable'
            }
            onClick={() =>
              setDeviceDrill({
                open: true,
                filter: 'out-of-date-ext',
                title: 'Devices not on the latest Nucleos extension',
              })
            }
          />
          <StatCard
            label="Synced 24h"
            value={`${deviceStats.pctWithin24h}%`}
            color="#1b5e20"
            bg="#e8f5e9"
            subline={`${deviceStats.histogram.within1d.toLocaleString()} devices — click for active`}
            onClick={() =>
              setDeviceDrill({
                open: true,
                filter: 'active',
                title: 'Active devices (synced)',
              })
            }
          />
          <StatCard
            label="Synced 7d"
            value={`${deviceStats.pctWithin7d}%`}
            color="#0277bd"
            bg="#e1f5fe"
            subline="click to see >7d stale"
            onClick={() =>
              setDeviceDrill({
                open: true,
                filter: 'stale-7d',
                title: 'Devices not synced in 7+ days',
              })
            }
          />
          <StatCard
            label="Stale (>30d)"
            value={(
              deviceStats.histogram.within90d + deviceStats.histogram.older
            ).toLocaleString()}
            color="#b71c1c"
            bg="#ffebee"
            subline="needs investigation — click to drill"
            onClick={() =>
              setDeviceDrill({
                open: true,
                filter: 'stale-30d',
                title: 'Devices not synced in 30+ days',
              })
            }
          />
        </Stack>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Filter by OU path"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="fleet-template-filter-label">Template (filter)</InputLabel>
            <Select
              labelId="fleet-template-filter-label"
              label="Template (filter)"
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
            >
              <MenuItem value="all">All templates</MenuItem>
              {usedTemplateNames.map((tname) => (
                <MenuItem key={tname} value={tname}>
                  {templateOptions.find((t) => t.name === tname)?.displayName ||
                    tname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="fleet-status-label">Status</InputLabel>
            <Select
              labelId="fleet-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="compliant">Compliant</MenuItem>
              <MenuItem value="drift">Drift</MenuItem>
              <MenuItem value="missing">Missing policies</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="not-audited">Not audited</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {filteredRows.length} / {rows.length} rows
          </Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
              <TableCell sortDirection={sortKey === 'ou' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'ou'}
                  direction={sortKey === 'ou' ? sortDir : 'asc'}
                  onClick={() => toggleSort('ou')}
                >
                  Organizational Unit
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'template' ? sortDir : false} sx={{ minWidth: 240 }}>
                <TableSortLabel
                  active={sortKey === 'template'}
                  direction={sortKey === 'template' ? sortDir : 'asc'}
                  onClick={() => toggleSort('template')}
                >
                  Template
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'status' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'status'}
                  direction={sortKey === 'status' ? sortDir : 'asc'}
                  onClick={() => toggleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortKey === 'match' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'match'}
                  direction={sortKey === 'match' ? sortDir : 'asc'}
                  onClick={() => toggleSort('match')}
                >
                  Match
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortKey === 'drift' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'drift'}
                  direction={sortKey === 'drift' ? sortDir : 'asc'}
                  onClick={() => toggleSort('drift')}
                >
                  Drift
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortKey === 'missing' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'missing'}
                  direction={sortKey === 'missing' ? sortDir : 'asc'}
                  onClick={() => toggleSort('missing')}
                >
                  Missing
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortKey === 'extra' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'extra'}
                  direction={sortKey === 'extra' ? sortDir : 'asc'}
                  onClick={() => toggleSort('extra')}
                >
                  Extra
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'auditedAt' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'auditedAt'}
                  direction={sortKey === 'auditedAt' ? sortDir : 'asc'}
                  onClick={() => toggleSort('auditedAt')}
                >
                  Audited
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Active / inactive devices in this OU (Admin SDK Directory API).">
                  <span>Devices</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="% of devices running the latest Nucleos extension version (Chrome Management API).">
                  <span>Latest ext</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Median age of last device sync. Color: green <7d, yellow <30d, red >=30d.">
                  <span>Last sync</span>
                </Tooltip>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {rows.length === 0
                      ? 'No data yet — the OU directory is empty or unreachable.'
                      : 'No rows match the current filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((r) => {
              const assignment = assignmentByOuId[r.ouId];
              const currentTemplate = effectiveTemplate(r);
              const isSuggested = !assignment || assignment.suggested === true;
              const isSaving = !!savingOuIds[r.ouId];
              const justSaved = !!recentlySaved[r.ouId];
              return (
              <TableRow
                key={r.ouId}
                hover
                sx={{
                  cursor: r.auditId ? 'pointer' : 'default',
                  bgcolor:
                    r.status === 'missing'
                      ? 'rgba(255, 235, 238, 0.4)'
                      : r.status === 'drift'
                      ? 'rgba(255, 243, 224, 0.35)'
                      : undefined,
                }}
                onClick={() => r.auditId && onOpenAudit?.(r.auditId)}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {r.ouPath}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {r.ouId}
                  </Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={
                          templateOptions.some((t) => t.name === currentTemplate)
                            ? currentTemplate
                            : ''
                        }
                        onChange={(e) => saveAssignment(r, e.target.value)}
                        disabled={isSaving || templateOptions.length === 0}
                        renderValue={(value) => {
                          if (!value) {
                            return (
                              <Typography variant="body2" color="text.disabled">
                                (no template)
                              </Typography>
                            );
                          }
                          const t = templateOptions.find((x) => x.name === value);
                          const label = t?.displayName || value;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontStyle: isSuggested ? 'italic' : 'normal',
                                  color: isSuggested ? 'text.secondary' : 'text.primary',
                                }}
                              >
                                {label}
                              </Typography>
                              {isSuggested && (
                                <Chip
                                  size="small"
                                  label="suggested"
                                  sx={{ ml: 1, height: 18, fontSize: 10 }}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          );
                        }}
                      >
                        {templateOptions.map((t) => (
                          <MenuItem key={t.name} value={t.name}>
                            {t.displayName || t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {isSaving && <CircularProgress size={14} />}
                    {justSaved && (
                      <Tooltip title="Assignment saved (visible to all users).">
                        <CheckIcon fontSize="small" color="success" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{statusChip(r.status)}</TableCell>
                <TableCell align="center">{countCell(r.match, 'match')}</TableCell>
                <TableCell align="center">{countCell(r.drift, 'drift')}</TableCell>
                <TableCell align="center">{countCell(r.missing, 'missing')}</TableCell>
                <TableCell align="center">{countCell(r.extra, 'extra')}</TableCell>
                <TableCell>
                  {r.auditedAt ? (
                    <Tooltip title={new Date(r.auditedAt).toLocaleString()}>
                      <Typography variant="body2" color="text.secondary">
                        {timeAgo(r.auditedAt)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <DeviceCountCell agg={deviceAggByOuId[r.ouId]} />
                </TableCell>
                <TableCell align="center">
                  <LatestExtCell
                    agg={deviceAggByOuId[r.ouId]}
                    chromeMgmtEnabled={chromeMgmtEnabled}
                  />
                </TableCell>
                <TableCell align="center">
                  <LastSyncCell agg={deviceAggByOuId[r.ouId]} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View per-device drill-down">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrillOuPath(r.ouPath);
                            setDrillOuId(r.ouId);
                          }}
                        >
                          <DevicesIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {r.auditId ? (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenAudit?.(r.auditId);
                        }}
                      >
                        Open
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <OuDeviceDrillDown
        token={token}
        ouPath={drillOuPath}
        ouId={drillOuId}
        open={Boolean(drillOuPath)}
        onClose={() => {
          setDrillOuPath(null);
          setDrillOuId(null);
        }}
        agg={drillOuId ? deviceAggByOuId[drillOuId] : null}
      />

      <FleetDeviceDrillDialog
        token={token}
        open={deviceDrill.open}
        initialFilter={deviceDrill.filter}
        title={deviceDrill.title}
        onClose={() => setDeviceDrill((prev) => ({ ...prev, open: false }))}
      />

      {deviceLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Loading device fleet data…
        </Typography>
      )}

      {/* "Run all" confirmation */}
      <Dialog
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run audit on every OU?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will fire a read-only audit for every OU ({rows.length} total)
            against its currently-assigned template. Heuristic suggestions are
            used for OUs without an explicit assignment.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            READ-ONLY — no Google policies are written. Each audit hits the
            Chrome Policy API and persists a diff record inside Automatrix.
            Runs with a concurrency cap of 4.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)} disabled={bulkStarting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={startBulkAudit}
            disabled={bulkStarting}
            startIcon={<RunIcon />}
          >
            {bulkStarting ? 'Starting…' : 'Run all audits'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk progress dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => {
          if (
            bulkJob &&
            (bulkJob.status === 'completed' ||
              bulkJob.status === 'failed' ||
              bulkJob.status === 'cancelled')
          ) {
            setBulkDialogOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bulk audit{' '}
          {bulkJob && (
            <Chip
              size="small"
              sx={{ ml: 1 }}
              label={bulkJob.status}
              color={
                bulkJob.status === 'completed'
                  ? 'success'
                  : bulkJob.status === 'failed'
                  ? 'error'
                  : bulkJob.status === 'cancelled'
                  ? 'warning'
                  : 'info'
              }
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {bulkJob && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Running {bulkJob.done}/{bulkJob.total} audits
                {bulkJob.succeeded > 0 && (
                  <Chip
                    size="small"
                    label={`${bulkJob.succeeded} ok`}
                    color="success"
                    sx={{ ml: 1 }}
                  />
                )}
                {bulkJob.failed > 0 && (
                  <Chip
                    size="small"
                    label={`${bulkJob.failed} failed`}
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
                {bulkJob.skipped > 0 && (
                  <Chip
                    size="small"
                    label={`${bulkJob.skipped} skipped`}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  bulkJob.total > 0
                    ? Math.round((bulkJob.done / bulkJob.total) * 100)
                    : 0
                }
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                {bulkJob.results.map((row) => (
                  <Box
                    key={row.ouId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'completed'
                          ? 'success'
                          : row.status === 'failed'
                          ? 'error'
                          : row.status === 'running'
                          ? 'info'
                          : row.status === 'skipped'
                          ? 'warning'
                          : 'default'
                      }
                      sx={{ minWidth: 90 }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {row.ouPath}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {row.templateName}
                    </Typography>
                    {row.error && (
                      <Tooltip title={row.error}>
                        <ErrorIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {bulkJob && bulkJob.status === 'running' && (
            <Button onClick={cancelBulkAudit} color="warning">
              Cancel pending
            </Button>
          )}
          <Button
            onClick={() => setBulkDialogOpen(false)}
            disabled={!!bulkJob && bulkJob.status === 'running'}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Per-OU device drill-down dialog ───────────────────────────────────────
export function OuDeviceDrillDown({ token, ouPath, ouId, open, onClose, agg }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staleOnly, setStaleOnly] = useState(false);

  const load = useCallback(async () => {
    if (!ouPath) return;
    setLoading(true);
    setError(null);
    setDevices([]);
    try {
      const resp = await fetch(
        `/api/google-ou-compliance/devices?ou=${encodeURIComponent(ouPath)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.message || `HTTP ${resp.status}`);
      }
      setDevices(data.devices || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [ouPath, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return devices.filter((d) => {
      if (statusFilter !== 'all') {
        const s = (d.status || '').toUpperCase();
        if (statusFilter === 'active' && s !== 'ACTIVE') return false;
        if (statusFilter === 'inactive' && (s === 'ACTIVE' || s === 'DEPROVISIONED'))
          return false;
        if (statusFilter === 'deprovisioned' && s !== 'DEPROVISIONED') return false;
      }
      if (staleOnly) {
        if (!d.lastSync) return true;
        const ageMs = Date.now() - new Date(d.lastSync).getTime();
        if (ageMs < 30 * 24 * 3600 * 1000) return false;
      }
      if (!q) return true;
      const hay = [
        d.serialNumber,
        d.model,
        d.osVersion,
        d.annotatedUser,
        d.annotatedLocation,
        ...(d.recentUsers || []).map((u) => u.email),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [devices, filter, statusFilter, staleOnly]);

  const stats = useMemo(() => {
    let active = 0;
    let stale = 0;
    let deprovisioned = 0;
    const ages = [];
    for (const d of devices) {
      const s = (d.status || '').toUpperCase();
      if (s === 'ACTIVE') active += 1;
      else if (s === 'DEPROVISIONED') deprovisioned += 1;
      if (d.lastSync) {
        const age = Date.now() - new Date(d.lastSync).getTime();
        ages.push(age);
        if (age > 30 * 24 * 3600 * 1000) stale += 1;
      } else {
        stale += 1;
      }
    }
    ages.sort((a, b) => a - b);
    const median = ages.length > 0 ? ages[Math.floor(ages.length / 2)] : null;
    return {
      total: devices.length,
      active,
      deprovisioned,
      stale,
      activePct:
        devices.length > 0 ? Math.round((active / devices.length) * 100) : 0,
      medianAgeMs: median,
    };
  }, [devices]);

  const exportCsv = useCallback(() => {
    const cols = [
      'serialNumber',
      'model',
      'osVersion',
      'platformVersion',
      'status',
      'bootMode',
      'lastSync',
      'lastEnrollmentTime',
      'annotatedUser',
      'annotatedLocation',
      'macAddress',
      'lastKnownIp',
      'extensionVersion',
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(',')];
    for (const d of filtered) {
      const lastIp =
        Array.isArray(d.lastKnownNetwork) && d.lastKnownNetwork[0]
          ? d.lastKnownNetwork[0].ipAddress || d.lastKnownNetwork[0].wanIpAddress
          : '';
      const extVer = (agg?.versionHistogram || [])[0]?.version || '';
      lines.push(
        cols
          .map((c) => {
            if (c === 'lastKnownIp') return esc(lastIp);
            if (c === 'extensionVersion') return esc(extVer);
            return esc(d[c]);
          })
          .join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safePath = (ouPath || 'ou').replace(/[^a-z0-9]/gi, '_');
    a.download = `devices-${safePath}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered, ouPath, agg]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DevicesIcon />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Devices — {ouPath || ''}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}>
          <StatCard
            label="Devices"
            value={stats.total}
            color="#1976d2"
            bg="#e3f2fd"
          />
          <StatCard
            label="Active"
            value={`${stats.activePct}%`}
            color="#1b5e20"
            bg="#e8f5e9"
            subline={`${stats.active} / ${stats.total}`}
          />
          <StatCard
            label="Stale (>30d)"
            value={stats.stale}
            color={stats.stale > 0 ? '#b71c1c' : '#616161'}
            bg={stats.stale > 0 ? '#ffebee' : '#f5f5f5'}
          />
          <StatCard
            label="Deprovisioned"
            value={stats.deprovisioned}
            color="#616161"
            bg="#f5f5f5"
          />
          <StatCard
            label="Median sync age"
            value={
              stats.medianAgeMs == null ? '—' : humanizeMs(stats.medianAgeMs)
            }
            color={
              stats.medianAgeMs == null
                ? '#616161'
                : stats.medianAgeMs < 7 * 24 * 3600 * 1000
                ? '#1b5e20'
                : stats.medianAgeMs < 30 * 24 * 3600 * 1000
                ? '#e65100'
                : '#b71c1c'
            }
            bg={
              stats.medianAgeMs == null
                ? '#f5f5f5'
                : stats.medianAgeMs < 7 * 24 * 3600 * 1000
                ? '#e8f5e9'
                : stats.medianAgeMs < 30 * 24 * 3600 * 1000
                ? '#fff3e0'
                : '#ffebee'
            }
          />
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder="Filter serial / model / user…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="drill-status">Status</InputLabel>
            <Select
              labelId="drill-status"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="deprovisioned">Deprovisioned</MenuItem>
            </Select>
          </FormControl>
          <Chip
            label="Stale (>30d)"
            color={staleOnly ? 'error' : 'default'}
            onClick={() => setStaleOnly((v) => !v)}
            variant={staleOnly ? 'filled' : 'outlined'}
            clickable
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {filtered.length} / {devices.length} devices
          </Typography>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            CSV
          </Button>
          <IconButton onClick={load} size="small" disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                <TableCell>Serial</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>OS version</TableCell>
                <TableCell>Last sync</TableCell>
                <TableCell>Last user</TableCell>
                <TableCell>Last IP</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No devices match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((d) => {
                const ageMs = d.lastSync
                  ? Date.now() - new Date(d.lastSync).getTime()
                  : null;
                const isStale = ageMs == null || ageMs > 30 * 24 * 3600 * 1000;
                const isDeprov = (d.status || '').toUpperCase() === 'DEPROVISIONED';
                const lastIp =
                  Array.isArray(d.lastKnownNetwork) && d.lastKnownNetwork[0]
                    ? d.lastKnownNetwork[0].ipAddress ||
                      d.lastKnownNetwork[0].wanIpAddress
                    : null;
                const lastUser =
                  d.annotatedUser ||
                  (Array.isArray(d.recentUsers) && d.recentUsers[0]?.email) ||
                  null;
                return (
                  <TableRow
                    key={d.deviceId}
                    sx={{
                      bgcolor: isDeprov
                        ? 'rgba(245, 245, 245, 0.6)'
                        : isStale
                        ? 'rgba(255, 235, 238, 0.45)'
                        : undefined,
                      color: isDeprov ? 'text.disabled' : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {d.serialNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        {d.model || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {d.osVersion || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {d.lastSync ? (
                        <Tooltip title={new Date(d.lastSync).toLocaleString()}>
                          <Typography
                            variant="body2"
                            color={isStale ? 'error' : 'text.secondary'}
                          >
                            {timeAgo(d.lastSync)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="error">
                          never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {lastUser || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {lastIp || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.status || '—'}
                        color={
                          isDeprov
                            ? 'default'
                            : (d.status || '').toUpperCase() === 'ACTIVE'
                            ? 'success'
                            : 'warning'
                        }
                        variant={isDeprov ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Fleet-wide device drill-down dialog ────────────────────────────────────
//
// Opens when a clickable tile on the Fleet tab is selected. Renders a paged,
// sortable, filterable table of every Chrome OS device across every OU. The
// `initialFilter` prop maps to the tile that triggered the open. All
// filtering is client-side against the flat /devices/all payload so the
// user can change the drop-downs without re-fetching.
function FleetDeviceDrillDialog({ token, open, initialFilter, title, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allDevices, setAllDevices] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stateFilter, setStateFilter] = useState(initialFilter || 'all');
  const [ouFilter, setOuFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [extVerFilter, setExtVerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState('ouPath');
  const [sortDir, setSortDir] = useState('asc');

  // Reset the state filter every time the dialog re-opens against a new
  // tile so the user does not see stale chips.
  useEffect(() => {
    if (open) {
      setStateFilter(initialFilter || 'all');
      setPage(0);
    }
  }, [open, initialFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/devices/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.message || `HTTP ${resp.status}`);
      }
      setAllDevices(Array.isArray(data.devices) ? data.devices : []);
      setMeta(data.meta || null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open && allDevices.length === 0 && !loading) {
      load();
    }
  }, [open, allDevices.length, loading, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allDevices.filter((d) => {
      if (stateFilter !== 'all') {
        if (stateFilter === 'out-of-date-ext') {
          if (d.complianceState !== 'out-of-date-ext') return false;
        } else if (stateFilter === 'active') {
          if ((d.status || '').toUpperCase() !== 'ACTIVE') return false;
          if (
            d.complianceState !== 'compliant' &&
            d.complianceState !== 'out-of-date-ext'
          ) {
            return false;
          }
        } else if (stateFilter === 'stale-7d') {
          if (
            d.complianceState !== 'last-sync-stale-7d' &&
            d.complianceState !== 'last-sync-stale-30d'
          ) {
            return false;
          }
        } else if (stateFilter === 'stale-30d') {
          if (d.complianceState !== 'last-sync-stale-30d') return false;
        } else if (stateFilter === 'inactive') {
          if (d.complianceState !== 'inactive') return false;
        } else if (stateFilter === 'deprovisioned') {
          if (d.complianceState !== 'deprovisioned') return false;
        } else if (stateFilter === 'compliant') {
          if (d.complianceState !== 'compliant') return false;
        }
      }
      if (ouFilter !== 'all' && d.ouPath !== ouFilter) return false;
      if (modelFilter !== 'all' && (d.model || '') !== modelFilter) return false;
      if (extVerFilter !== 'all') {
        if (extVerFilter === '__unknown__') {
          if (d.extensionVersion) return false;
        } else if ((d.extensionVersion || '') !== extVerFilter) {
          return false;
        }
      }
      if (q) {
        const hay = [
          d.serialNumber,
          d.model,
          d.ouPath,
          d.osVersion,
          d.annotatedUser,
          d.lastIp,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allDevices, stateFilter, ouFilter, modelFilter, extVerFilter, search]);

  useEffect(() => {
    setPage(0);
  }, [stateFilter, ouFilter, modelFilter, extVerFilter, search, sortKey, sortDir]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      let av;
      let bv;
      if (sortKey === 'lastSync') {
        av = a.lastSync ? new Date(a.lastSync).getTime() : 0;
        bv = b.lastSync ? new Date(b.lastSync).getTime() : 0;
      } else {
        av = (a[sortKey] || '').toString().toLowerCase();
        bv = (b[sortKey] || '').toString().toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    };
    return [...filtered].sort(cmp);
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = page * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const ouOptions = useMemo(() => {
    const set = new Set(allDevices.map((d) => d.ouPath).filter(Boolean));
    return Array.from(set).sort();
  }, [allDevices]);
  const modelOptions = useMemo(() => {
    const set = new Set(allDevices.map((d) => d.model).filter(Boolean));
    return Array.from(set).sort();
  }, [allDevices]);
  const extVerOptions = useMemo(() => {
    const set = new Set();
    for (const d of allDevices) {
      if (d.extensionVersion) set.add(d.extensionVersion);
    }
    return Array.from(set).sort();
  }, [allDevices]);

  const exportCsv = useCallback(() => {
    const cols = [
      'serialNumber',
      'model',
      'ouPath',
      'lastSync',
      'lastIp',
      'osVersion',
      'extensionVersion',
      'latestExtensionVersion',
      'status',
      'complianceState',
      'annotatedUser',
      'annotatedLocation',
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(',')];
    for (const d of sorted) {
      lines.push(cols.map((c) => esc(d[c])).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devices-fleet-${stateFilter}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [sorted, stateFilter]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DevicesIcon />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="div">
            {title || 'Fleet devices'}
          </Typography>
          {meta && (
            <Typography variant="caption" color="text.secondary">
              {meta.total.toLocaleString()} devices across{' '}
              {meta.ouCountWithDevices} OUs · fetched{' '}
              {new Date(meta.fetchedAt).toLocaleTimeString()}
              {meta.truncated && (
                <Chip
                  size="small"
                  label={`truncated at ${meta.maxDevices}`}
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {meta && meta.chromeMgmtEnabled === false && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Chrome Management API is not enabled — per-device extension
            versions are not available.
            {meta.enableUrl && (
              <Button
                size="small"
                href={meta.enableUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<LaunchIcon />}
                sx={{ ml: 1 }}
              >
                Enable
              </Button>
            )}
          </Alert>
        )}
        {meta && Array.isArray(meta.errors) && meta.errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {meta.errors.length} OU{meta.errors.length === 1 ? '' : 's'}{' '}
            failed to fetch and were skipped.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <TextField
            size="small"
            placeholder="Search serial / model / OU / user / IP…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="fleet-drill-state">Compliance state</InputLabel>
            <Select
              labelId="fleet-drill-state"
              label="Compliance state"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="compliant">Compliant</MenuItem>
              <MenuItem value="out-of-date-ext">Out-of-date extension</MenuItem>
              <MenuItem value="active">Active (synced &lt;7d)</MenuItem>
              <MenuItem value="stale-7d">Stale (&gt;7d)</MenuItem>
              <MenuItem value="stale-30d">Stale (&gt;30d)</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="deprovisioned">Deprovisioned</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="fleet-drill-ou">OU</InputLabel>
            <Select
              labelId="fleet-drill-ou"
              label="OU"
              value={ouFilter}
              onChange={(e) => setOuFilter(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="all">All OUs</MenuItem>
              {ouOptions.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="fleet-drill-model">Model</InputLabel>
            <Select
              labelId="fleet-drill-model"
              label="Model"
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="all">All models</MenuItem>
              {modelOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="fleet-drill-ext">Ext version</InputLabel>
            <Select
              labelId="fleet-drill-ext"
              label="Ext version"
              value={extVerFilter}
              onChange={(e) => setExtVerFilter(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="all">All versions</MenuItem>
              <MenuItem value="__unknown__">Unknown</MenuItem>
              {extVerOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {sorted.length.toLocaleString()} /{' '}
            {allDevices.length.toLocaleString()} devices
          </Typography>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={sorted.length === 0}
          >
            CSV
          </Button>
          <IconButton onClick={load} size="small" disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 540 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                <TableCell sortDirection={sortKey === 'serialNumber' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'serialNumber'}
                    direction={sortKey === 'serialNumber' ? sortDir : 'asc'}
                    onClick={() => toggleSort('serialNumber')}
                  >
                    Serial
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortKey === 'model' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'model'}
                    direction={sortKey === 'model' ? sortDir : 'asc'}
                    onClick={() => toggleSort('model')}
                  >
                    Model
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortKey === 'ouPath' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'ouPath'}
                    direction={sortKey === 'ouPath' ? sortDir : 'asc'}
                    onClick={() => toggleSort('ouPath')}
                  >
                    OU
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortKey === 'lastSync' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'lastSync'}
                    direction={sortKey === 'lastSync' ? sortDir : 'asc'}
                    onClick={() => toggleSort('lastSync')}
                  >
                    Last sync
                  </TableSortLabel>
                </TableCell>
                <TableCell>Last IP</TableCell>
                <TableCell sortDirection={sortKey === 'osVersion' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'osVersion'}
                    direction={sortKey === 'osVersion' ? sortDir : 'asc'}
                    onClick={() => toggleSort('osVersion')}
                  >
                    OS
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ext version</TableCell>
                <TableCell>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 4 }}
                    >
                      No devices match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {paged.map((d) => {
                const ageMs = d.lastSync
                  ? Date.now() - new Date(d.lastSync).getTime()
                  : null;
                const isStale30 = ageMs == null || ageMs > 30 * 24 * 3600 * 1000;
                const isStale7 = ageMs != null && ageMs > 7 * 24 * 3600 * 1000;
                const isDeprov =
                  (d.status || '').toUpperCase() === 'DEPROVISIONED';
                const stateColor =
                  d.complianceState === 'compliant'
                    ? 'success'
                    : d.complianceState === 'out-of-date-ext'
                    ? 'warning'
                    : d.complianceState === 'last-sync-stale-7d'
                    ? 'warning'
                    : d.complianceState === 'last-sync-stale-30d'
                    ? 'error'
                    : 'default';
                const stateLabel =
                  d.complianceState === 'last-sync-stale-7d'
                    ? 'stale 7d'
                    : d.complianceState === 'last-sync-stale-30d'
                    ? 'stale 30d'
                    : d.complianceState === 'out-of-date-ext'
                    ? 'old ext'
                    : d.complianceState;
                return (
                  <TableRow
                    key={d.deviceId}
                    sx={{
                      bgcolor: isDeprov
                        ? 'rgba(245,245,245,0.6)'
                        : isStale30
                        ? 'rgba(255,235,238,0.45)'
                        : isStale7
                        ? 'rgba(255,243,224,0.45)'
                        : undefined,
                      color: isDeprov ? 'text.disabled' : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {d.serialNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {d.model || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={d.ouId}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                          {d.ouPath}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {d.lastSync ? (
                        <Tooltip title={new Date(d.lastSync).toLocaleString()}>
                          <Typography
                            variant="body2"
                            color={
                              isStale30
                                ? 'error'
                                : isStale7
                                ? 'warning.main'
                                : 'text.secondary'
                            }
                          >
                            {timeAgo(d.lastSync)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="error">
                          never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {d.lastIp || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {d.osVersion || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {d.extensionVersion ? (
                        <Tooltip
                          title={
                            d.latestExtensionVersion &&
                            d.extensionVersion === d.latestExtensionVersion
                              ? "On the OU's latest version"
                              : d.latestExtensionVersion
                              ? `OU latest: ${d.latestExtensionVersion}`
                              : 'No OU-wide version known'
                          }
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {d.extensionVersion}
                          </Typography>
                        </Tooltip>
                      ) : d.latestExtensionVersion ? (
                        <Tooltip title="Per-device extension version unavailable — Chrome Management API only surfaces per-OU aggregates when an OU has >1 version installed.">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {d.latestExtensionVersion}*
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={stateLabel}
                        color={stateColor}
                        variant={
                          d.complianceState === 'compliant' ? 'filled' : 'outlined'
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="fleet-drill-pagesize">Page size</InputLabel>
            <Select
              labelId="fleet-drill-pagesize"
              label="Page size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexGrow: 1 }}
          >
            Page {sorted.length === 0 ? 0 : page + 1} of{' '}
            {Math.max(1, Math.ceil(sorted.length / pageSize))}
          </Typography>
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="small"
            disabled={(page + 1) * pageSize >= sorted.length}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Per-OU device cell components ──────────────────────────────────────────
function DeviceCountCell({ agg }) {
  if (!agg) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  if (agg.error) {
    return (
      <Tooltip title={agg.error}>
        <Typography variant="body2" color="error">
          err
        </Typography>
      </Tooltip>
    );
  }
  if (!agg.deviceCount) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Tooltip
      title={
        <Box>
          <div>Active: {agg.activeCount}</div>
          <div>Inactive: {agg.inactiveCount}</div>
          <div>Deprovisioned: {agg.deprovisionedCount}</div>
        </Box>
      }
    >
      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
      >
        {agg.activeCount} / {agg.inactiveCount + agg.deprovisionedCount}
      </Typography>
    </Tooltip>
  );
}

function LatestExtCell({ agg, chromeMgmtEnabled }) {
  if (!agg) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  if (!chromeMgmtEnabled || agg.latestExtensionPct == null) {
    return (
      <Tooltip title="Chrome Management API data unavailable.">
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </Tooltip>
    );
  }
  const pct = agg.latestExtensionPct;
  const fg = pct >= 95 ? '#1b5e20' : pct >= 80 ? '#e65100' : '#b71c1c';
  const bg = pct >= 95 ? '#e8f5e9' : pct >= 80 ? '#fff3e0' : '#ffebee';
  const versions = agg.versionHistogram || [];
  return (
    <Tooltip
      title={
        <Box>
          <div style={{ fontWeight: 600 }}>
            Latest: {agg.latestExtensionVersion || '—'}
          </div>
          {versions.map((v) => (
            <div key={v.version}>
              {v.version}: {v.count}
            </div>
          ))}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-block',
          minWidth: 48,
          textAlign: 'center',
          bgcolor: bg,
          color: fg,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontWeight: 700,
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {pct}%
      </Box>
    </Tooltip>
  );
}

function LastSyncCell({ agg }) {
  if (!agg) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  if (agg.lastSyncMedianAgeMs == null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const age = agg.lastSyncMedianAgeMs;
  const fg =
    age < 7 * 24 * 3600 * 1000
      ? '#1b5e20'
      : age < 30 * 24 * 3600 * 1000
      ? '#e65100'
      : '#b71c1c';
  const bg =
    age < 7 * 24 * 3600 * 1000
      ? '#e8f5e9'
      : age < 30 * 24 * 3600 * 1000
      ? '#fff3e0'
      : '#ffebee';
  const h = agg.lastSyncHistogram || {};
  return (
    <Tooltip
      title={
        <Box>
          <div>Median: {humanizeMs(age)}</div>
          <div>&lt;1d: {h.within1d || 0}</div>
          <div>&lt;7d: {h.within7d || 0}</div>
          <div>&lt;30d: {h.within30d || 0}</div>
          <div>&lt;90d: {h.within90d || 0}</div>
          <div>older: {h.older || 0}</div>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-block',
          minWidth: 48,
          textAlign: 'center',
          bgcolor: bg,
          color: fg,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        {humanizeMs(age)}
      </Box>
    </Tooltip>
  );
}

function humanizeMs(ms) {
  if (ms == null) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 60) return `${d}d`;
  const mo = Math.floor(d / 30);
  return `${mo}mo`;
}

function StatCard({ label, value, color, bg, subline, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <Paper
      elevation={0}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        flex: 1,
        p: 2,
        bgcolor: bg,
        border: `1px solid ${color}22`,
        borderRadius: 2,
        minWidth: 140,
        cursor: clickable ? 'pointer' : 'default',
        transition:
          'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': clickable
          ? {
              transform: 'translateY(-1px)',
              boxShadow: 2,
              borderColor: `${color}66`,
            }
          : undefined,
        '&:focus-visible': clickable
          ? { outline: `2px solid ${color}`, outlineOffset: 2 }
          : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color, fontWeight: 700, mt: 0.5, lineHeight: 1 }}>
        {value}
      </Typography>
      {subline && (
        <Typography variant="caption" sx={{ color, opacity: 0.75, display: 'block', mt: 0.5 }}>
          {subline}
        </Typography>
      )}
    </Paper>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'in the future';
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Tab: Tenant Policies ──────────────────────────────────────────────────
//
// Workspace-tenant templates (SSO, 2SV, password policy, Drive sharing, MDM,
// license assignments, customer/domain config, context-aware access) audit
// at the Google customer scope — there is no concept of "this template
// against /Sites/WDOC/Kiosk". Google scopes these settings tenant-wide, so
// running them per-OU would be a category error.
//
// This tab lists every workspace-tenant template, shows the latest audit
// summary for each, and provides a "Run audit" button that POSTs the audit
// against the tenant-root (`ouId: '/'`). The View details link reuses the
// existing AuditDetail view via the parent page's tab forwarding.
export function TenantPoliciesTab({ token, onOpenAudit }) {
  const [templates, setTemplates] = useState([]);
  const [latestByTemplate, setLatestByTemplate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningName, setRunningName] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tplResp, auditsResp] = await Promise.all([
        fetch(
          '/api/google-ou-compliance/templates?appliesTo=workspace-tenant',
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        // Pull a generous window so the "latest per template" lookup is
        // accurate even after a long backlog of correctional audits.
        fetch('/api/google-ou-compliance/audits?limit=500', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!tplResp.ok) throw new Error(`Templates: HTTP ${tplResp.status}`);
      if (!auditsResp.ok) throw new Error(`Audits: HTTP ${auditsResp.status}`);
      const tplData = await tplResp.json();
      const auditData = await auditsResp.json();
      const tpls = tplData.data || [];
      // Deduplicate by name — keep highest version. The /templates endpoint
      // sorts name ASC, version DESC, so first-seen wins.
      const dedup = Array.from(
        tpls
          .reduce((m, t) => {
            if (!m.has(t.name)) m.set(t.name, t);
            return m;
          }, new Map())
          .values(),
      );
      setTemplates(dedup);

      // Find the latest audit row per workspace-tenant template.
      const workspaceNames = new Set(dedup.map((t) => t.name));
      const audits = auditData.data || [];
      const latest = {};
      for (const a of audits) {
        if (!workspaceNames.has(a.templateName)) continue;
        const prev = latest[a.templateName];
        if (
          !prev ||
          new Date(a.startedAt).getTime() > new Date(prev.startedAt).getTime()
        ) {
          latest[a.templateName] = a;
        }
      }
      setLatestByTemplate(latest);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const runAuditFor = useCallback(
    async (template) => {
      setRunningName(template.name);
      setFlash(null);
      setError(null);
      try {
        const resp = await fetch('/api/google-ou-compliance/audits/run', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            templateName: template.name,
            templateVersion: template.version,
            // Workspace-tenant templates audit at the customer scope. We use
            // the tenant-root sentinel `/` — the backend recognizes this and
            // routes through Phase 2A fetchers instead of Chrome Policy API.
            ouId: '/',
            ouDisplayName: 'Tenant (workspace)',
            source: 'live',
            notes: 'Tenant Policies tab — workspace-tenant audit',
            generateAiExplanations: false,
          }),
        });
        if (!resp.ok) {
          const t = await resp.text();
          throw new Error(`HTTP ${resp.status}: ${t}`);
        }
        const data = await resp.json();
        setFlash({
          kind: 'success',
          message: `Audit completed: ${data.summary?.matchCount ?? 0} match / ${data.summary?.driftCount ?? 0} drift / ${data.summary?.missingCount ?? 0} missing.`,
          auditId: data.auditId,
        });
        await load();
      } catch (e) {
        setError(`Audit failed for ${template.name}: ${e.message || String(e)}`);
      } finally {
        setRunningName(null);
      }
    },
    [token, load],
  );

  const summaryChip = (audit) => {
    if (!audit) {
      return (
        <Chip
          size="small"
          label="never audited"
          variant="outlined"
          sx={{ color: 'text.disabled' }}
        />
      );
    }
    const s = audit.summaryStats || {};
    const drift = s.driftCount ?? 0;
    const missing = s.missingCount ?? 0;
    const match = s.matchCount ?? 0;
    let color = 'success';
    let label = `${match} match`;
    if (missing > 0) {
      color = 'error';
      label = `${missing} missing`;
    } else if (drift > 0) {
      color = 'warning';
      label = `${drift} drift`;
    }
    return <Chip size="small" label={label} color={color} />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Tenant Policies
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        These audit tenant-wide Workspace configuration (SSO, 2SV, password
        policy, Drive sharing, MDM, etc.). Google scopes these at the customer
        level, not per-OU — so we run them once against the whole Workspace.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {flash && (
        <Alert
          severity={flash.kind}
          sx={{ mb: 2 }}
          onClose={() => setFlash(null)}
          action={
            flash.auditId ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => onOpenAudit?.(flash.auditId)}
              >
                View details
              </Button>
            ) : null
          }
        >
          {flash.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No workspace-tenant templates loaded. Drop a YAML with{' '}
              <code>appliesTo: workspace-tenant</code> into{' '}
              <code>templates/google-ou/</code> and reload.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                <TableCell>Template</TableCell>
                <TableCell align="right">Policies</TableCell>
                <TableCell>Latest audit</TableCell>
                <TableCell>Last run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => {
                const audit = latestByTemplate[t.name];
                const policyCount = Array.isArray(t.body?.policies)
                  ? t.body.policies.length
                  : 0;
                const isRunning = runningName === t.name;
                return (
                  <TableRow key={t.name} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t.displayName || t.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {t.name} v{t.version}
                      </Typography>
                      {t.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {t.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{policyCount}</TableCell>
                    <TableCell>{summaryChip(audit)}</TableCell>
                    <TableCell>
                      {audit ? (
                        <Tooltip title={new Date(audit.startedAt).toLocaleString()}>
                          <Typography variant="body2" color="text.secondary">
                            {timeAgo(audit.startedAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {audit && (
                          <Button
                            size="small"
                            onClick={() => onOpenAudit?.(audit.id)}
                          >
                            View details
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            isRunning ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <RunIcon />
                            )
                          }
                          onClick={() => runAuditFor(t)}
                          disabled={isRunning || !!runningName}
                        >
                          {isRunning ? 'Running…' : 'Run audit'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── Tab: Settings ─────────────────────────────────────────────────────────
export function SettingsTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/dwd-status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setStatus(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const testConnection = async () => {
    setWorking(true);
    setError(null);
    setFlash(null);
    try {
      const resp = await fetch('/api/google-ou-compliance/dwd-status/verify', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          data?.message || `HTTP ${resp.status} — DWD verify failed`,
        );
      }
      setFlash({
        severity: 'success',
        text: `Token mint succeeded — expires ${new Date(data.expiresAt).toLocaleString()}.`,
      });
      await load();
    } catch (e) {
      setFlash({ severity: 'error', text: e.message || String(e) });
    } finally {
      setWorking(false);
    }
  };

  const keyFileStatus = (() => {
    if (loading || !status) return { label: 'Loading…', color: 'default' };
    if (status.configured) return { label: 'Found', color: 'success' };
    const errs = status.errors || [];
    if (errs.some((e) => /not found/i.test(e))) {
      return { label: 'Not Found', color: 'error' };
    }
    if (errs.some((e) => /EACCES|not readable/i.test(e))) {
      return { label: 'Unreadable', color: 'error' };
    }
    if (errs.some((e) => /JSON|missing required fields/i.test(e))) {
      return { label: 'Malformed', color: 'error' };
    }
    return { label: 'Not Configured', color: 'warning' };
  })();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Domain-Wide Delegation Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Google OU Compliance Auditor uses a Service Account with
        Domain-Wide Delegation (DWD) to read Chrome and Workspace policies
        across customer Workspaces. Same <code>.readonly</code> scopes as
        before; no per-user OAuth consent required.
      </Typography>

      {flash && (
        <Alert severity={flash.severity} sx={{ mb: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Service Account
        </Typography>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <Box>
            <Typography variant="body2">
              SA email:{' '}
              <code>{status?.saClientEmail || '(unknown — SA key not loaded)'}</code>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              SA client ID (the 21-digit string to paste into the Workspace
              Admin Console for DWD authorization):{' '}
              <code>{status?.saClientId || '(unknown — SA key not loaded)'}</code>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Impersonating: <code>{status?.impersonateUser || '(not set)'}</code>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Key file path: <code>{status?.saKeyPath}</code>{' '}
              <Chip size="small" label={keyFileStatus.label} color={keyFileStatus.color} sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Configured:{' '}
              <Chip
                size="small"
                label={status?.configured ? 'yes' : 'no'}
                color={status?.configured ? 'success' : 'warning'}
              />
            </Typography>
            {status?.lastVerifiedAt && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Last verified: <strong>{new Date(status.lastVerifiedAt).toLocaleString()}</strong>
              </Typography>
            )}
            {(status?.errors || []).length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Setup incomplete</AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {status.errors.map((e, i) => (
                    <li key={i}><Typography variant="body2">{e}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={testConnection}
                disabled={working}
              >
                {working ? 'Testing…' : 'Test connection'}
              </Button>
              <Button
                variant="text"
                onClick={load}
                disabled={loading || working}
                sx={{ ml: 1 }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Read-only scopes (always)
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {(status?.scopes || [
            'https://www.googleapis.com/auth/chrome.management.policy.readonly',
            'https://www.googleapis.com/auth/admin.directory.orgunit.readonly',
          ]).map((s) => (
            <li key={s}>
              <code style={{ fontSize: 11 }}>{s}</code>
            </li>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          No write scopes are ever requested. Defensive check refuses to mint
          a token with any non-<code>.readonly</code> scope.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Setup checklist
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 3 }}>
          <li>
            <Typography variant="body2">
              In the dedicated GCP project (<code>automatrix-ou-manager</code>),
              create a Service Account and generate a JSON key.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              SCP the key JSON to <code>{status?.saKeyPath || '/etc/automatrix/sa-keys/ou-auditor.json'}</code>{' '}
              (chmod 600; parent dir chmod 700).
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              In the target Workspace Admin Console
              (<em>Security → API controls → Domain-wide delegation</em>),
              authorize the SA's client ID for these two scopes only:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <code style={{ fontSize: 11 }}>
                  https://www.googleapis.com/auth/chrome.management.policy.readonly
                </code>
              </li>
              <li>
                <code style={{ fontSize: 11 }}>
                  https://www.googleapis.com/auth/admin.directory.orgunit.readonly
                </code>
              </li>
            </Box>
          </li>
          <li>
            <Typography variant="body2">
              In the backend <code>.env</code>, set{' '}
              <code>GOOGLE_OU_IMPERSONATE_USER</code> to a Workspace
              super-admin email in that domain, then run{' '}
              <code>pm2 reload automatrix-api --update-env</code>.
            </Typography>
          </li>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Why no write scopes?
        </Typography>
        <Typography variant="body2">
          Write-back is explicitly out of scope per the project plan. Admins
          remediate manually in the Google Admin Console using the deep-link
          rendered next to each drift entry. This keeps the security surface
          small and makes customer onboarding easier across separate
          Workspaces (Scenario B).
        </Typography>
      </Paper>
    </Box>
  );
}

// ── Top-level page ────────────────────────────────────────────────────────
export default function ComplianceGoogleOUPage() {
  // Default to the Fleet tab — it's the demo money shot and reads existing data.
  const [tab, setTab] = useState(1);
  // When the Fleet tab forwards a click, jump to the Audits tab pre-opened on
  // the audit detail view.
  const [forwardAuditId, setForwardAuditId] = useState(null);
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const openAuditFromFleet = (auditId) => {
    setForwardAuditId(auditId);
    setTab(3);
  };

  return (
    <Box sx={{ p: 2 }}>
      <ReadOnlyBanner />

      <Typography variant="h4" gutterBottom>
        Google OU Compliance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Audits Chromebook / tablet Google Workspace OU policies against
        versioned templates. Read-only, deep-link-to-fix remediation. Project
        plan:{' '}
        <a
          href="https://www.notion.so/37369d3aaf9e81438abce51aa38a92c1"
          target="_blank"
          rel="noopener noreferrer"
        >
          Notion
        </a>
        .
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary">
          <Tab label="Templates" />
          <Tab label="Fleet" />
          <Tab label="Tenant Policies" />
          <Tab label="Audits" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {tab === 0 && <TemplatesTab token={token} />}
      {tab === 1 && <FleetTab token={token} onOpenAudit={openAuditFromFleet} />}
      {tab === 2 && (
        <TenantPoliciesTab token={token} onOpenAudit={openAuditFromFleet} />
      )}
      {tab === 3 && (
        <AuditsTab
          token={token}
          openWithAuditId={forwardAuditId}
          key={forwardAuditId || 'default'}
        />
      )}
      {tab === 4 && <SettingsTab token={token} />}
    </Box>
  );
}
